/**
 * One login tip nudge per browser session (sessionStorage). Cleared on sign out.
 * Tip JSON in storage survives React Strict remounts so the card does not flash away in dev.
 */

export const LOGIN_NUDGE_TIP = 'st_login_nudge_tip';
export const LOGIN_NUDGE_IMPRESSION = 'st_login_nudge_impression';
export const LOGIN_NUDGE_DISMISSED = 'st_login_nudge_dismissed';

export const SESSION_RADAR_OPENED = 'st_session_radar_opened';
export const SESSION_STAG_OPENED = 'st_session_stag_opened';
export const SESSION_WORK_OPENED = 'st_session_work_opened';

const ALL_KEYS = [
  LOGIN_NUDGE_TIP,
  LOGIN_NUDGE_IMPRESSION,
  LOGIN_NUDGE_DISMISSED,
  SESSION_RADAR_OPENED,
  SESSION_STAG_OPENED,
  SESSION_WORK_OPENED,
];

function ssGet(k) {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage.getItem(k) : null;
  } catch (_) {
    return null;
  }
}

function ssSet(k, v) {
  try {
    if (typeof window !== 'undefined') window.sessionStorage.setItem(k, v);
  } catch (_) { /* no-op */ }
}

function ssRemove(k) {
  try {
    if (typeof window !== 'undefined') window.sessionStorage.removeItem(k);
  } catch (_) { /* no-op */ }
}

export function isLoginNudgeDismissedThisSession() {
  return ssGet(LOGIN_NUDGE_DISMISSED) === '1';
}

export function readPersistedLoginTip() {
  if (isLoginNudgeDismissedThisSession()) return null;
  const raw = ssGet(LOGIN_NUDGE_TIP);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

export function persistLoginTip(tip) {
  ssSet(LOGIN_NUDGE_TIP, JSON.stringify(tip));
}

export function isLoginNudgeImpressionLogged() {
  return ssGet(LOGIN_NUDGE_IMPRESSION) === '1';
}

export function markLoginNudgeImpression() {
  ssSet(LOGIN_NUDGE_IMPRESSION, '1');
}

export function markLoginNudgeDismissed() {
  ssSet(LOGIN_NUDGE_DISMISSED, '1');
  ssRemove(LOGIN_NUDGE_TIP);
  ssRemove(LOGIN_NUDGE_IMPRESSION);
}

/** Call on sign out so the next login can show a tip again. */
export function clearLoginNudgeSessionKeys() {
  try {
    if (typeof window === 'undefined') return;
    ALL_KEYS.forEach((k) => {
      try {
        window.sessionStorage.removeItem(k);
      } catch (_) { /* no-op */ }
    });
  } catch (_) { /* no-op */ }
}

export const LOGIN_TIPS = [
  {
    id: 'radar',
    title: 'Solo map',
    text: 'Radar lists people out solo in your city right now. Send a vibe, match, then chat for the day.',
    action: 'Open Radar',
    path: '/radar',
  },
  {
    id: 'bars',
    title: 'Solo friendly places',
    text: 'Browse bars and spots that welcome stags, with cover and seating notes so you are not guessing.',
    action: 'Find a bar',
    path: '/stag-venues',
  },
  {
    id: 'work',
    title: 'Work friendly cafés',
    text: 'Filter for WiFi, power, and quieter spots when you need to get work done out of the house.',
    action: 'Work spots',
    path: '/work',
  },
  {
    id: 'shout',
    title: 'City Shout',
    text: 'Post a short line to your whole city. Good for who is around or a quick plan.',
    action: 'Open the board',
    path: '/matches',
  },
  {
    id: 'profile',
    title: 'Trust on your profile',
    text: 'Add a clear photo, verify Instagram, and a short bio so Radar and Shout feel safer for everyone.',
    action: 'Edit profile',
    path: '/profile/edit',
  },
];

function byId(id) {
  return LOGIN_TIPS.find((t) => t.id === id) || LOGIN_TIPS[0];
}

function randomTip() {
  return LOGIN_TIPS[Math.floor(Math.random() * LOGIN_TIPS.length)];
}

/**
 * Nudge the next useful surface the user has not opened this session, then fall back to random.
 */
export function pickLoginTip({ unified } = {}) {
  const openedRadar = ssGet(SESSION_RADAR_OPENED) === '1';
  const openedStag = ssGet(SESSION_STAG_OPENED) === '1';
  const openedWork = ssGet(SESSION_WORK_OPENED) === '1';

  if (!openedRadar) return byId('radar');

  const insta = unified?.user?.verification?.instagram;
  if (insta && insta.status && insta.status !== 'approved') {
    return byId('profile');
  }
  if (!unified?.user?.bio?.trim()) {
    return byId('profile');
  }

  if (!openedStag) return byId('bars');
  if (!openedWork) return byId('work');

  if (Math.random() < 0.5) return byId('shout');
  return randomTip();
}
